import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    applyLkqPricingRuleChange,
    groupLkqPricingRules,
    removeLkqPricingRule,
} from '../../../../utils/lkqPricingRows';
import PricingTableSection from './pricing/PricingTableSection';
import type { PricingTableTab } from './pricing/PricingTableSection';
import {
    PRICING_TABLES,
    percentageOf,
    pricingEntriesOf,
    pricingEntryFactory,
    pricingRowPath,
    pricingUpdate,
    type PricingEntry,
    type PricingTableSpec,
} from './pricing/pricingTables';
import { useInvoicingDraft, usePricingRowNames } from './shared';
import type { InvoicingSectionProps } from './types';

const SECTION_ID = 'invoicing-discounts';

const DiscountsSection = ({ data, organizationStates }: InvoicingSectionProps) => {
    const { invoicing, updateInvoicing, isSaving } = useInvoicingDraft(data);
    const navigate = useNavigate();

    const serverRows = useMemo(
        () => [...(data.lkqDiscounts ?? []), ...(data.subletDiscounts ?? [])],
        [data.lkqDiscounts, data.subletDiscounts],
    );
    const { withNames, rememberNames } = usePricingRowNames(serverRows);

    /** One table's draft entries, with the display names re-attached. */
    const entriesOf = useCallback(
        (spec: PricingTableSpec): PricingEntry[] => withNames(pricingEntriesOf(spec, invoicing)),
        [withNames, invoicing],
    );

    const lkqSpec = PRICING_TABLES[SECTION_ID][0];
    const subletSpec = PRICING_TABLES[SECTION_ID][1];

    /** Build a tab from its spec: the copy and the writes are all it differs by. */
    const buildTab = (spec: PricingTableSpec): PricingTableTab => {
        const entries = entriesOf(spec);
        const readPercentage = percentageOf(spec);
        return {
            id: spec.tabId,
            label: spec.tabLabel,
            rows: groupLkqPricingRules(entries, readPercentage),
            percentColumnLabel: spec.percentColumnLabel,
            emptyText: spec.emptyText,
            addButtonText: 'Add Row',
            rowNoun: spec.rowNoun,
            // Adding and editing are the same full-screen rule editor,
            // which owns its own save; 'new' is the add case.
            onAdd: () => navigate(pricingRowPath(SECTION_ID, spec.tabId, 'new')),
            onEditRow: (rule) => navigate(pricingRowPath(SECTION_ID, spec.tabId, rule.ruleKey)),
            onSaveRow: (rule, values) => {
                // The update payload carries ids only, so a newly picked
                // vendor's name is kept beside the draft until the next refetch.
                rememberNames([
                    {
                        insuranceCompanyId: rule.insuranceCompanyId,
                        insuranceCompanyName: rule.insuranceCompanyName,
                        supplierId: values.supplierId,
                        supplierName: values.supplierName,
                    },
                ]);
                updateInvoicing(
                    pricingUpdate(
                        spec,
                        applyLkqPricingRuleChange(
                            entries,
                            rule,
                            values,
                            readPercentage,
                            pricingEntryFactory(spec),
                        ),
                    ),
                );
            },
            onRemoveRow: (ruleKey) =>
                updateInvoicing(
                    pricingUpdate(spec, removeLkqPricingRule(entries, ruleKey, readPercentage)),
                ),
        };
    };

    return (
        <PricingTableSection
            organizationStates={organizationStates}
            isSaving={isSaving}
            tabs={[buildTab(lkqSpec), buildTab(subletSpec)]}
        />
    );
};

export default DiscountsSection;
